import type { Row } from "@tanstack/react-table";
import { isFuture } from "date-fns";
import type { TFunction } from "i18next";

import { ActionIcon, Icon, TableAction } from "src/components";
import { ConfirmContextConsumer } from "src/modal";
import { ICON_TABLE_SIZE } from "src/table";
import { Color } from "src/theme";
import {
    type ExpandTableSpecialPricesType,
    type SpecialPriceDto,
    CustomerCredibilityEnum,
    isSpecialPriceDto,
    SpecialPriceSourceEnum,
} from "src/types";
import { isTodayInDateRange } from "src/utils";

const PRODUCT_BRAND_DEPTH_LEVEL = 0;

export const getCustomerDetailTextColor = (isInactive: boolean) => (isInactive ? Color.supportNavy300 : Color.supportNavy500);

export const getCustomerCredibilityColor = (isInactive: boolean, credibility?: CustomerCredibilityEnum) => {
    switch (credibility) {
        case CustomerCredibilityEnum.Good:
            return Color.success500;
        case CustomerCredibilityEnum.Suspicious:
            return Color.warning600;
        case CustomerCredibilityEnum.Bad:
            return Color.error500;
        default:
            return getCustomerDetailTextColor(isInactive);
    }
};

export const createDeleteSpecialPriceButton = (t: TFunction, onConfirm: () => void, disabled?: boolean, disabledTooltip?: string) => (
    <ConfirmContextConsumer>
        {({ showConfirm }) => {
            const confirm = async () => {
                const confirmResult = await showConfirm({
                    title: t("specialPrices.delete.confirm.title"),
                    content: t("specialPrices.delete.confirm.content"),
                });
                if (confirmResult) {
                    onConfirm();
                }
            };
            return (
                <ActionIcon
                    disabled={disabled}
                    onClick={confirm}
                    color={Color.error100}
                    tooltip={disabled ? disabledTooltip : t("specialPrices.deleteTooltip")}
                >
                    <Icon.Delete size={ICON_TABLE_SIZE} color={disabled ? Color.neutral400 : Color.error500} />
                </ActionIcon>
            );
        }}
    </ConfirmContextConsumer>
);

const isDeleteButtonVisible = (specialPrice?: SpecialPriceDto) => specialPrice && isFuture(new Date(specialPrice.validFrom));

const isEditButtonVisible = (specialPrice?: SpecialPriceDto) => {
    if (!specialPrice) {
        return false;
    }

    return (
        isFuture(new Date(specialPrice.validFrom)) || isTodayInDateRange(new Date(specialPrice.validFrom), new Date(specialPrice.validTo))
    );
};

export const createSpecialPriceActionRow =
    (
        t: TFunction,
        openProductPriceFormModal: (specialPriceId: number) => void,
        deleteProductPrice: (specialPriceId: number) => void,
        openSpecialPriceAttachment: (specialPriceId: number) => void,
        specialPriceSource: SpecialPriceSourceEnum,
    ) =>
    (row: Row<ExpandTableSpecialPricesType>): JSX.Element => {
        const specialPrice = isSpecialPriceDto(row.original) ? row.original : undefined;
        const isBulkSpecialPrice = !!specialPrice?.bulkId;
        const isFromDifferentSource = specialPriceSource !== specialPrice?.specialPriceSource;
        const isDisabled = isFromDifferentSource || isBulkSpecialPrice;
        const differentSourceDisabledTooltip =
            specialPriceSource === SpecialPriceSourceEnum.Payer ? t("specialPrices.actionFromShipTo") : t("specialPrices.actionFromPayer");
        const disabledTooltip = isBulkSpecialPrice ? t("specialPrices.actionFromBulkCreate") : differentSourceDisabledTooltip;

        return (
            <TableAction>
                {specialPrice && (
                    <>
                        <ActionIcon
                            disabled={isDisabled}
                            onClick={() => openSpecialPriceAttachment(specialPrice.id)}
                            color={Color.accent100}
                            tooltip={isDisabled ? disabledTooltip : t("specialPrices.attachmentTooltip")}
                        >
                            <Icon.Attach size={ICON_TABLE_SIZE} color={isDisabled ? Color.neutral400 : Color.primary600} filled />
                        </ActionIcon>
                        {isEditButtonVisible(specialPrice) && (
                            <ActionIcon
                                disabled={isDisabled}
                                tooltip={isDisabled ? disabledTooltip : t("specialPrices.editTooltip")}
                                onClick={() => openProductPriceFormModal(specialPrice.id)}
                                color={Color.accent100}
                            >
                                <Icon.Edit size={ICON_TABLE_SIZE} color={isDisabled ? Color.neutral400 : Color.primary600} filled />
                            </ActionIcon>
                        )}
                        {isDeleteButtonVisible(specialPrice) &&
                            createDeleteSpecialPriceButton(t, () => deleteProductPrice(specialPrice.id), isDisabled, disabledTooltip)}
                    </>
                )}
            </TableAction>
        );
    };

export const overrideSpecialPriceRowProps = (row: Row<ExpandTableSpecialPricesType>): Record<string, unknown> => {
    if (row.depth === PRODUCT_BRAND_DEPTH_LEVEL) {
        return { $backgroundColor: Color.neutral50 };
    }
    // @ts-ignore
    if (row.original.active === false) {
        return { $backgroundColor: Color.supportGraphite50 };
    }
    return { $backgroundColor: Color.white };
};
